import React, { useEffect, useState } from 'react';
import './App.css';
import LeafletMap from './LeafletMap';
import logoImg from './LOGO_ECOSTAR-removebg-preview.png';

type BasemapChoice = 'felt' | 'satellite' | 'osm' | 'bnetd';

// URL d'embed officielle Felt (prévue pour l'intégration dans des sites)
const FELT_MAP_URL =
  'https://felt.com/embed/map/Getting-started-with-Felt-VCyhVeiCQyKZTapE8ko5aD';

async function fetchRoute(startLabel: string, endLabel: string): Promise<{
  distanceKm: number;
  durationMin: number;
}> {
  const response = await fetch('/api/route', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ startLabel, endLabel }),
  });

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => null);
    const errorMessage =
      (errorPayload && (errorPayload.error as string | undefined)) ||
      `Échec du calcul d’itinéraire (HTTP ${response.status})`;
    throw new Error(errorMessage);
  }

  const data: { distanceKm: number; durationMin: number } = await response.json();
  return data;
}

type ParcelleStats = {
  count: number;
  surface_total: number | null;
};

type StatsPayload = {
  parcelles: {
    exploitant_espace_autorise: ParcelleStats;
    exploitant_foret_classee: ParcelleStats;
    exploitant_zone_eaux: ParcelleStats;
  };
};

type Village = {
  name: string;
  lat: number;
  lon: number;
  region?: string | null;
};

function App() {
  const [stats, setStats] = useState<StatsPayload | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [routeMessage, setRouteMessage] = useState<string | null>(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError, setRouteError] = useState<string | null>(null);
  const [surfaceMode, setSurfaceMode] = useState<'region' | 'exploitant' | 'zone'>('region');
  const [surfaceMessage, setSurfaceMessage] = useState<string | null>(null);
  const [surfaceError, setSurfaceError] = useState<string | null>(null);
  const [basemap, setBasemap] = useState<BasemapChoice>('felt');
  const [villages, setVillages] = useState<Village[]>([]);
  const [startVillage, setStartVillage] = useState<string>('');
  const [endVillage, setEndVillage] = useState<string>('');

  useEffect(() => {
    fetch('/stats.json')
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((data: StatsPayload) => {
        setStats(data);
      })
      .catch((error) => {
        // On loggue seulement dans la console pour ne pas gêner l'UI
        console.warn('Impossible de charger stats.json :', error);
        setStatsError('Statistiques non disponibles pour le moment.');
      });

    fetch('/villages.json')
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((data: Village[]) => {
        setVillages(data);
      })
      .catch((error) => {
        console.warn('Impossible de charger villages.json :', error);
      });
  }, []);

  return (
    <div className="cotton-app">
      <aside className="cotton-sidebar">
        <header className="cotton-sidebar-header">
          <h1>PRODUCTEUR COTON</h1>
          <p>Cartographie professionnelle des producteurs de coton en Côte d&apos;Ivoire</p>
        </header>

        <section className="cotton-panel">
          <h2>Légende</h2>
          <ul className="legend-list">
            <li>
              <span className="legend-dot legend-zone-eau"></span>
              Exploitant en zone d&apos;eaux
            </li>
            <li>
              <span className="legend-dot legend-foret-classee-expl"></span>
              Exploitant en forêt classée
            </li>
            <li>
              <span className="legend-dot legend-espace-autorise"></span>
              Exploitant sur espace autorisé
            </li>
            <li>
              <span className="legend-line legend-secondary-road"></span>
              Route secondaire
            </li>
            <li>
              <span className="legend-line legend-primary-road"></span>
              Route primaire
            </li>
            <li>
              <span className="legend-line legend-track"></span>
              Autres (pistes / routes diverses)
            </li>
            <li>
              <span className="legend-color legend-water"></span>
              Eau
            </li>
            <li>
              <span className="legend-color legend-forest"></span>
              Base des Forêt Classée de CI
            </li>
          </ul>
        </section>

        <section className="cotton-panel">
          <h2>Fond de carte</h2>
          <div className="basemap-buttons">
            <button
              type="button"
              className={`basemap-button ${basemap === 'felt' ? 'basemap-active' : ''}`}
              onClick={() => setBasemap('felt')}
            >
              Carte Felt (couches coton)
            </button>
            <button
              type="button"
              className={`basemap-button ${basemap === 'satellite' ? 'basemap-active' : ''}`}
              onClick={() => setBasemap('satellite')}
            >
              Satellite hybride
            </button>
            <button
              type="button"
              className={`basemap-button ${basemap === 'osm' ? 'basemap-active' : ''}`}
              onClick={() => setBasemap('osm')}
            >
              Carte routière
            </button>
            <button
              type="button"
              className={`basemap-button ${basemap === 'bnetd' ? 'basemap-active' : ''}`}
              onClick={() => setBasemap('bnetd')}
            >
              Occupation du sol (BNETD)
            </button>
          </div>
          <p className="basemap-hint">
            {basemap === 'felt'
              ? 'Carte Felt avec toutes les couches producteur.'
              : 'Fond affiché dans la zone carte à droite.'}
          </p>
        </section>

      </aside>

      <main className="cotton-map-container">
        <div className="map-wrapper">
          {basemap === 'felt' ? (
            <iframe
              title="Carte PRODUCTEUR COTON"
              src={FELT_MAP_URL}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            <LeafletMap basemap={basemap} />
          )}
          <div className="company-logo">
            <img src={logoImg} alt="Logo Ecostar" />
          </div>
        </div>
      </main>

      <div className="cotton-panels-row">
        <section className="cotton-panel">
            <h2>Règles &amp; filtres</h2>
            <form
              className="cotton-form"
              onSubmit={(event) => {
                event.preventDefault();
              }}
            >
              <div className="form-group">
                <label htmlFor="region-select">Région</label>
                <select id="region-select" className="cotton-select" defaultValue="">
                  <option value="" disabled>
                    Toutes les régions
                  </option>
                  <option value="bagoue">Bagoué</option>
                  <option value="bere">Béré</option>
                  <option value="kabadougou">Kabadougou</option>
                  <option value="poro">Poro</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="surface-min">Surface totale minimale (ha)</label>
                <input
                  id="surface-min"
                  className="cotton-input"
                  type="number"
                  min={0}
                  placeholder="Ex. 5"
                />
              </div>

              <div className="form-group-inline">
                <label className="checkbox-label">
                  <input type="checkbox" defaultChecked />
                  <span>Mettre en avant les écarts &gt; 20 %</span>
                </label>
              </div>

              <p className="panel-caption">
                Ces paramètres serviront à appliquer les règles de style et de contrôle métier dans
                Felt (parcelles en anomalie, zones prioritaires, etc.).
              </p>
            </form>
          </section>

          <section className="cotton-panel">
            <h2>Itinéraire</h2>
            <form
              className="cotton-form"
              onSubmit={async (event) => {
                event.preventDefault();
                setRouteError(null);
                setRouteMessage(null);

              const start = villages.find((v) => v.name === startVillage);
              const end = villages.find((v) => v.name === endVillage);

              if (!start || !end) {
                setRouteMessage('Choisis un village de départ et un village d’arrivée.');
                return;
              }

                try {
                  setRouteLoading(true);
                const { distanceKm, durationMin } = await fetchRoute(start.name, end.name);
                  const distance = distanceKm.toFixed(1);
                  const duration = Math.round(durationMin);
                  setRouteMessage(
                  `Itinéraire estimé entre « ${start.name} » et « ${end.name} » : ` +
                      `${distance} km pour environ ${duration} min de trajet.`
                  );
                } catch (error) {
                  const message =
                    error instanceof Error ? error.message : "Erreur lors du calcul d’itinéraire.";
                  setRouteError(message);
                } finally {
                  setRouteLoading(false);
                }
              }}
            >
              <div className="form-group">
                <label htmlFor="start-village">Village de départ</label>
                <select
                  id="start-village"
                  className="cotton-select"
                  value={startVillage}
                  onChange={(event) => setStartVillage(event.target.value)}
                >
                  <option value="">Sélectionne un village</option>
                  {villages.slice(0, 500).map((v) => (
                    <option key={`start-${v.name}`} value={v.name}>
                      {v.name}
                      {v.region ? ` (${v.region})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="end-village">Village d’arrivée</label>
                <select
                  id="end-village"
                  className="cotton-select"
                  value={endVillage}
                  onChange={(event) => setEndVillage(event.target.value)}
                >
                  <option value="">Sélectionne un village</option>
                  {villages.slice(0, 500).map((v) => (
                    <option key={`end-${v.name}`} value={v.name}>
                      {v.name}
                      {v.region ? ` (${v.region})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <button type="submit" className="cotton-button">
                Calculer l’itinéraire
              </button>

              <p className="panel-caption">
                À terme, ce bouton appellera un service de routage (ex. OpenRouteService) pour
                afficher le tracé directement sur la carte.
              </p>

              {routeLoading && (
                <p className="panel-caption panel-caption-strong">
                  Calcul de l’itinéraire en cours…
                </p>
              )}

              {routeError && (
                <p className="panel-caption panel-caption-strong">{routeError}</p>
              )}

              {routeMessage && <p className="panel-caption panel-caption-strong">{routeMessage}</p>}
            </form>
          </section>

          <section className="cotton-panel">
            <h2>Calcul de surface</h2>
            <form
              className="cotton-form"
              onSubmit={(event) => {
                event.preventDefault();
                setSurfaceError(null);
                setSurfaceMessage(null);

                if (!stats) {
                  setSurfaceError('Les statistiques ne sont pas encore chargées.');
                  return;
                }

                const base = stats.parcelles.exploitant_espace_autorise;
                if (typeof base.surface_total !== 'number') {
                  setSurfaceError(
                    "Aucune surface totale n’est disponible dans les données actuelles."
                  );
                  return;
                }

                const totalParcelles = base.count.toLocaleString('fr-FR');
                const totalSurface = new Intl.NumberFormat('fr-FR', {
                  maximumFractionDigits: 0,
                }).format(base.surface_total);

                if (surfaceMode === 'region') {
                  setSurfaceMessage(
                    `Surface totale coton (espace autorisé) : ${totalSurface} ha sur ${totalParcelles} parcelles. ` +
                      'Le détail par région sera calculé lors de la prochaine étape (agrégation par région).'
                  );
                } else if (surfaceMode === 'exploitant') {
                  setSurfaceMessage(
                    `Au total, ${totalSurface} ha de coton (espace autorisé) sur ${totalParcelles} parcelles. ` +
                      'Le détail par exploitant sera ajouté ensuite via des agrégations côté backend.'
                  );
                } else {
                  setSurfaceMessage(
                    `Surface globale coton (espace autorisé) : ${totalSurface} ha. ` +
                      'Le calcul précis sur une zone dessinée sera branché avec Leaflet Draw dans une version suivante.'
                  );
                }
              }}
            >
              <div className="form-group">
                <label htmlFor="surface-mode">Type d’agrégat</label>
                <select
                  id="surface-mode"
                  className="cotton-select"
                  value={surfaceMode}
                  onChange={(event) =>
                    setSurfaceMode(event.target.value as 'region' | 'exploitant' | 'zone')
                  }
                >
                  <option value="region">Par région</option>
                  <option value="exploitant">Par exploitant</option>
                  <option value="zone">Sur zone dessinée</option>
                </select>
              </div>

              <button type="submit" className="cotton-button">
                Calculer les surfaces
              </button>

              <p className="panel-caption">
                Cette section sera reliée aux attributs des couches Felt pour sommer les surfaces
                coton / autres cultures sur la zone choisie.
              </p>

              {surfaceError && (
                <p className="panel-caption panel-caption-strong">{surfaceError}</p>
              )}

              {surfaceMessage && (
                <p className="panel-caption panel-caption-strong">{surfaceMessage}</p>
              )}

              {stats && (
                <div className="stats-summary">
                  <div>
                    <span className="stats-label">Parcelles coton (espace autorisé)</span>
                    <span className="stats-value">
                      {stats.parcelles.exploitant_espace_autorise.count.toLocaleString('fr-FR')}{' '}
                      parcelles
                    </span>
                  </div>
                  {typeof stats.parcelles.exploitant_espace_autorise.surface_total === 'number' && (
                    <div>
                      <span className="stats-label">Surface totale estimée</span>
                      <span className="stats-value">
                        {new Intl.NumberFormat('fr-FR', {
                          maximumFractionDigits: 0,
                        }).format(stats.parcelles.exploitant_espace_autorise.surface_total)}{' '}
                        ha
                      </span>
                    </div>
                  )}
                </div>
              )}

              {statsError && !stats && (
                <p className="panel-caption">{statsError}</p>
              )}
            </form>
          </section>
      </div>
    </div>
  );
}

export default App;
